// CTV-682 rev 3: "Get your business on TV this week" — the leave-behind a rep
// gives a prospective advertiser. Built from the KITV design draft (Aug 2026).
// The two versions it replaces live in ./archive/v2 and ./archive/v1.
//
// Departures from the draft, and why:
//   • Every filled block uses the station's derived accent, not the draft's
//     fixed navy and cyan. Those happened to be KITV's brand colours; hard-
//     coding them would make every station's sheet look like KITV's.
//     readableAccent() is safe both as text on paper and under white text on
//     a fill, so one tone covers the pill, the cards and the CTA.
//   • Paper is the house #faf9f7, not the draft's cream.
//   • The TV is the landing page's own chrome asset (public/tv-chrome.webp),
//     with the screen insets copied from SelfServiceLanding so the sheet's TV
//     and the site's are calibrated identically.
//   • The TV screen drops the draft's ":30" — we accept :15 and :30, and the
//     getting-started sheet is where the specs belong.
//   • The draft's em dash is a hyphen, per house style.
//
// The copy answers what the advertiser needs to decide, not how we operate.
// "No contract" is accurate in the sense they use it: no insertion order, no
// term commitment, no early-termination penalty. Step 3 gives them the
// timing ("this week"), which holds — review runs 24-48 hours against a
// three-day minimum start — without walking them through our workflow.
//
// The city in step 1 and the locality chip come from the station's single
// market; a multi-market station falls back to generic wording rather than
// naming one market and misdescribing the rest.

import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Check, Clock, CreditCard, Film, LineChart, MapPin, Play, Sliders, Tv } from 'lucide-react';
import type { PublisherConfig } from '../../../types/selfService';
import MaterialSheet from './MaterialSheet';
import { storefrontHref, storefrontUrl } from './storefrontUrl';
import { displayedName } from '../../../data/publisherConfigs';
import { marketCity } from './marketLabel';
import { formatDeviceList, resolveStationDevices } from '../streamingDeviceDisplay';

const AdvertiserOnePager: React.FC<{ config: PublisherConfig; refCode?: string | null }> = ({
  config,
  refCode,
}) => {
  const name = displayedName(config);
  const city = marketCity(config);
  const minBudget = Number(config.minBudget ?? 0).toLocaleString('en-US');
  // CTV-810: the station's selected devices (empty selection falls back to
  // the full canonical set inside resolveStationDevices).
  const deviceLabels = resolveStationDevices(config.devices).map((d) => d.label);

  return (
    <MaterialSheet
      config={config}
      variant="poster"
      kicker={<>For our advertisers<br />Streaming TV · self-serve</>}
      footNote={`© ${name} · Streaming TV placements in your local market`}
    >
      <div className="msheet-hero">
        <div>
          <div className="msheet-eyebrow">New from {name}</div>
          <h1>
            Get your business on TV <em>this week</em>
          </h1>
          <p className="msheet-sub">
            Advertise on the local channel you know and love. Streaming on{' '}
            {formatDeviceList(deviceLabels)}, with no agency and no contract.
          </p>
          <div className="msheet-pill">
            <span>Budgets from</span>
            <b>
              <i>$</i>
              {minBudget}
            </b>
          </div>
        </div>

        <div>
          <div className="msheet-tv">
            <img src={`${import.meta.env.BASE_URL}tv-chrome.webp`} alt="" aria-hidden />
            <div className="msheet-tv-screen">
              <div className="msheet-tv-live">Your spot · live</div>
              <strong>YOUR AD HERE</strong>
              <em>a real commercial, made for you</em>
              <div className="msheet-tv-bar">
                <Play fill="currentColor" strokeWidth={0} aria-hidden />
                <span className="msheet-tv-track" />
                <span className="msheet-tv-time">0:30</span>
              </div>
            </div>
          </div>
          <div className="msheet-tv-cap">
            Seen on
            {/* Non-breaking spaces inside each name and before each separator,
                so a wrapped caption line never starts with a dangling "·" and
                a device name never splits across lines. */}
            <b>{deviceLabels.map((l) => l.replace(/ /g, ' ')).join(' · ')}</b>
          </div>
        </div>
      </div>

      <div className="msheet-mid">
        <div className="msheet-steps msheet-steps--cards">
          <div className="msheet-step">
            <div className="msheet-step-head">
              <span className="msheet-step-num">1</span>
              <Sliders className="msheet-step-icon" aria-hidden />
            </div>
            <h3>Set budget &amp; targeting</h3>
            <p>
              Pick your spend, your dates, and the areas{city ? ` of ${city}` : ''} you want to
              reach. As low as ${minBudget}.
            </p>
          </div>
          <div className="msheet-step">
            <div className="msheet-step-head">
              <span className="msheet-step-num">2</span>
              <Film className="msheet-step-icon" aria-hidden />
            </div>
            <h3>No ad? We&rsquo;ll make one</h3>
            <p>
              Already have a commercial? Upload it. If not, we build one free from your website
              in minutes.
            </p>
            <span className="msheet-step-tag">Free if you need it</span>
          </div>
          <div className="msheet-step">
            <div className="msheet-step-head">
              <span className="msheet-step-num">3</span>
              <CreditCard className="msheet-step-icon" aria-hidden />
            </div>
            <h3>Pay &amp; go live</h3>
            <p>Check out with a card. Your ad starts running this week.</p>
          </div>
        </div>

        <div className="msheet-why">
          <div>
            <div className="msheet-why-label">
              <Tv className="msheet-why-icon" aria-hidden /> Why streaming TV
            </div>
            <h4>You&rsquo;re on {name}</h4>
            <p>The same local news your neighbors stream every day - now with your ad in it.</p>
          </div>
          <div>
            <div className="msheet-why-label">
              <LineChart className="msheet-why-icon" aria-hidden /> Proof it ran
            </div>
            <h4>Track every play</h4>
            <p>See exactly when and where your ad aired. No mystery, no guesswork.</p>
          </div>
        </div>

        <div className="msheet-chips">
          <div className="msheet-chip">
            <Clock className="msheet-chip-icon" aria-hidden /> Set up in minutes
          </div>
          <div className="msheet-chip">
            <Film className="msheet-chip-icon" aria-hidden /> Free commercial
          </div>
          <div className="msheet-chip">
            <MapPin className="msheet-chip-icon" aria-hidden />{' '}
            {city ? `Local to ${city}` : 'Local to your market'}
          </div>
          <div className="msheet-chip">
            <Check className="msheet-chip-icon" aria-hidden /> No contract
          </div>
        </div>

        <div className="msheet-cta">
          <div className="msheet-cta-text">
            <div className="msheet-cta-label">Start here</div>
            <div className="msheet-cta-main">
              <strong>Be on TV this week</strong>
              <a
                href={storefrontHref(config.slug, refCode, {
                  content: 'advertiser_onepager_link',
                })}
                target="_blank"
                rel="noreferrer"
              >
                {storefrontUrl(config.slug, refCode)}
              </a>
            </div>
          </div>
          <div className="msheet-cta-qr">
            <QRCodeSVG
              value={storefrontHref(config.slug, refCode, {
                content: 'advertiser_onepager_qr',
              })}
              size={78}
              level="M"
            />
          </div>
        </div>
      </div>
    </MaterialSheet>
  );
};

export default AdvertiserOnePager;
